
import { useCallback, useMemo } from 'react';
import { logger } from '@/utils/production-logger';
import { useErrorBoundary } from './useErrorBoundary';

interface ErrorHandlingConfig {
  currentSessionId: string | null;
  userId: string | null;
  componentName: string;
}

interface ConsolidatedErrorHandlers {
  handleUsageLimitError: (error: Error) => boolean;
  handleAiServiceError: (error: Error, requestId: string) => void;
  handleGenericError: (error: unknown, context: string, requestId?: string) => void;
  handleMessageSaveError: (error: unknown, messageId: string, requestId?: string) => void;
  recoverFromError: (error: unknown, context: string, requestId?: string) => void;
  createCentralizedErrorCallback: (context: string) => (error: unknown, additionalContext?: any, requestId?: string) => void;
  createPerformanceErrorCallback: (operation: string) => (error: unknown, context: any) => void;
}

export const useErrorHandlingConsolidation = (
  config: ErrorHandlingConfig,
  onMessageAdd?: (message: any) => void,
  onMessageRemove?: (messageId: string) => void
): ConsolidatedErrorHandlers => {
  const errorBoundary = useErrorBoundary({
    currentSessionId: config.currentSessionId,
    userId: config.userId,
    onMessageAdd,
    onMessageRemove
  });

  // Centralized error callback factory
  const createCentralizedErrorCallback = useCallback((context: string) => {
    return (error: unknown, additionalContext?: any, requestId?: string) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      logger.error(`Centralized error handler: ${context}`, {
        error: errorMessage,
        context: additionalContext,
        requestId,
        sessionId: config.currentSessionId,
        userId: config.userId,
        component: config.componentName
      });

      errorBoundary.recoverFromError(error, context, requestId);
    };
  }, [config.currentSessionId, config.userId, config.componentName, errorBoundary]);

  // Performance-aware error callback with throttling
  const createPerformanceErrorCallback = useCallback((operation: string) => {
    return (error: unknown, context: any) => {
      logger.error(`Performance issue in ${operation}`, {
        error: error instanceof Error ? error.message : String(error),
        context,
        component: config.componentName,
        sessionId: config.currentSessionId
      });
    };
  }, [config.componentName, config.currentSessionId]);

  // Return consolidated handlers
  return useMemo(() => ({
    handleUsageLimitError: errorBoundary.handleUsageLimitError,
    handleAiServiceError: errorBoundary.handleAiServiceError,
    handleGenericError: errorBoundary.handleGenericError,
    handleMessageSaveError: errorBoundary.handleMessageSaveError,
    recoverFromError: errorBoundary.recoverFromError,
    createCentralizedErrorCallback,
    createPerformanceErrorCallback
  }), [
    errorBoundary.handleUsageLimitError,
    errorBoundary.handleAiServiceError,
    errorBoundary.handleGenericError,
    errorBoundary.handleMessageSaveError,
    errorBoundary.recoverFromError,
    createCentralizedErrorCallback,
    createPerformanceErrorCallback
  ]);
};
